// frontend/src/pages/AIPostCreation.js
import '../custom.css'

import React from 'react'
import {
  Container,
  Row,
  Col,
  Image,
  Button,
  Card,
  Accordion,
  Badge,
} from 'react-bootstrap'
import { LightningChargeFill, Magic, CheckCircleFill } from 'react-bootstrap-icons'

/* placeholder imagery (swap for real screenshots later) */
const draftMock =
  '/assets/logo/aipost.webp'
const brandVoiceMock =
  'https://images.pexels.com/photos/240222/pexels-photo-240222.jpeg?auto=compress&cs=tinysrgb&w=1600'
const visualMock =
  'https://images.pexels.com/photos/7256715/pexels-photo-7256715.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=550&w=1150'

export default function AIPostCreation () {
  return (
    <>
      {/* ───────── HERO ───────── */}
      <section className='full-bleed py-5 bg-primary-soft text-center'>
        <div className='mx-auto px-4 px-lg-5' style={{ maxWidth: 960 }}>
          <h1 className='display-5 fw-bold mb-3'>AI&nbsp;Post&nbsp;Creation</h1>
          <p className='lead mb-4'>
            Turn blank pages into scroll-stopping posts in seconds with our
            built-in AI assistant.
          </p>
          <Button variant='primary' size='lg' href='/register'>
            Get Started Free
          </Button>
        </div>
      </section>

      {/* ───────── DRAFT GENERATOR ───────── */}
      <section className='py-5'>
        <Container fluid='lg'>
          <Row className='align-items-center g-5'>
            <Col md={6}>
              <Image src={draftMock} alt='AI caption generator' fluid className='shadow-card rounded-4' />
            </Col>
            <Col md={6}>
              <h2 className='fw-bold mb-3 d-flex align-items-center gap-2'>
                <LightningChargeFill /> Idea → Draft in one click
              </h2>
              <p className='text-muted fs-5'>
                Drop a keyword or paste a blog link; our AI crafts a tailored caption,
                hashtag set, and emoji mix — optimised for every platform.
              </p>
              {[
                'Supports 30+ languages',
                'Tone sliders: casual → corporate',
                'Auto-shortens links',
                'GIF & sticker suggestions',
              ].map(txt => (
                <p key={txt} className='d-flex align-items-start gap-2 mb-2'>
                  <CheckCircleFill className='text-success mt-1' /> {txt}
                </p>
              ))}
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── BRAND VOICE ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg'>
          <Row className='align-items-center g-5 flex-md-row-reverse'>
            <Col md={6}>
              <Image src={brandVoiceMock} alt='Brand voice preset' fluid className='shadow-card rounded-4' />
            </Col>
            <Col md={6}>
              <h2 className='fw-bold mb-3 d-flex align-items-center gap-2'>
                <Magic /> Stay on-brand, every time
              </h2>
              <p className='text-muted fs-5'>
                Save <Badge bg='secondary'>voice presets</Badge> that lock in your
                brand’s style. Add rules like “always upbeat”, “use UK spelling”, or
                “include a CTA at the end”.
              </p>
              <p className='text-muted mb-0'>
                Team members simply select the preset and the AI does the rest —
                consistency, sorted.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── IMAGE / VISUAL SUGGESTION ───────── */}
      <section className='py-5'>
        <Container fluid='lg'>
          <Row className='align-items-center g-5'>
            <Col md={6}>
              <Image src={visualMock} alt='Image suggestion panel' fluid className='shadow-card rounded-4' />
            </Col>
            <Col md={6}>
              <h2 className='fw-bold mb-3'>Match visuals to captions</h2>
              <p className='text-muted fs-5'>
                Our Unsplash &amp; Pexels integration surfaces copyright-free
                images based on your caption — or generates on-brand thumbnails
                via AI image prompts.
              </p>
              <p className='text-muted mb-0'>Perfect thumbnails in one tap.</p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── BENEFIT CARDS ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg'>
          <Row className='justify-content-center mb-5'>
            <Col lg={7} className='text-center'>
              <h2 className='fw-bold mb-4'>Why creators love our AI</h2>
            </Col>
          </Row>

          <Row className='g-4'>
            {[
              {
                title: 'Save hours weekly',
                body: 'Batch-create a month of captions in the time it once took to write one.',
              },
              {
                title: 'Never run out of ideas',
                body: 'Prompt suggestions keep your content calendar stocked with fresh angles.',
              },
              {
                title: 'Boost engagement',
                body: 'Smart hashtag sets and personalised CTAs lift reach & clicks.',
              },
            ].map(({ title, body }, i) => (
              <Col md={4} key={i}>
                <Card className='h-100 shadow-card'>
                  <Card.Body>
                    <Card.Title className='fw-semibold'>{title}</Card.Title>
                    <Card.Text className='text-muted'>{body}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ───────── FAQ ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 720 }}>
          <h2 className='fw-bold mb-4 text-center'>AI Creation FAQs</h2>
          <Accordion flush defaultActiveKey='0'>
            {[
              [
                'Is the AI really free?',
                'Yes — every plan includes a generous monthly quota of AI credits. Use them for captions, re-writes and image prompts.',
              ],
              [
                'Can I edit the AI suggestions?',
                'Absolutely. Treat the AI as a creative partner: accept a draft as-is or tweak it before scheduling.',
              ],
              [
                'Will my brand data be used to train the model?',
                'No. Your private brand guidelines and content stay encrypted and are never used to retrain public models.',
              ],
            ].map(([q, a], idx) => (
              <Accordion.Item eventKey={String(idx)} key={idx}>
                <Accordion.Header>{q}</Accordion.Header>
                <Accordion.Body>{a}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Container>
      </section>

      {/* ───────── CTA ───────── */}
      <section className='full-bleed py-5 text-center bg-primary text-white'>
        <div className='mx-auto' style={{ maxWidth: 720 }}>
          <h3 className='fw-bold mb-3'>Create your first AI-powered post today</h3>
          <p className='lead mb-4'>
            Sign up now and generate 10 captions — on us.
          </p>
          <Button variant='light' size='lg' href='/register' className='me-3'>
            Start Free Trial
          </Button>
          <Button variant='outline-light' size='lg' href='/plans'>
            View Plans
          </Button>
        </div>
      </section>
    </>
  )
}
